//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, { useState } from 'react';
import { ChevronUp, ExternalLink } from 'lucide-react';
import { Card, CardContent } from '../../ui/card';
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from '../../ui/collapsible';
import type {
  ExecutionFollowUp,
  ExecutionReceiptStatus,
  ExecutionTerminalStatus,
  ExecutionVerificationEntry,
  StoredExecutionReceipt,
} from '../../../types/executionReceipt';

interface ExecutionReceiptCardProps {
  receipt: StoredExecutionReceipt;
  // How the execution itself ended, as the server mapped it. Only read for a
  // receipt stored before the receipt carried its own follow-up: 'needs_new_plan'
  // then stands in for follow_up. A receipt with a follow_up field is
  // self-describing.
  terminalStatus?: ExecutionTerminalStatus | null;
}

const STATUS_LABEL: Record<ExecutionReceiptStatus, string> = {
  completed: 'Completed',
  partial: 'Partial',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

const STATUS_STYLE: Record<ExecutionReceiptStatus, string> = {
  completed:
    'border-green-400 bg-green-100 text-green-800 dark:border-green-700 dark:bg-green-900/30 dark:text-green-300',
  partial:
    'border-amber-400 bg-amber-100 text-amber-800 dark:border-amber-600 dark:bg-amber-900/30 dark:text-amber-300',
  failed:
    'border-red-400 bg-red-100 text-red-800 dark:border-red-700 dark:bg-red-900/30 dark:text-red-300',
  cancelled:
    'border-slate-400 bg-slate-100 text-slate-700 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300',
};

const ADMISSION_LABEL: Record<ExecutionVerificationEntry['admission'], string> =
  {
    admitted: 'Allowed',
    refused: 'Refused',
    unknown: 'Unknown',
  };

// What the approval gate did for one call. null from the server means it
// could not establish it, which is shown as such rather than guessed.
const approvalText = (
  confirmation: ExecutionVerificationEntry['confirmation'],
): string => {
  if (confirmation.gate_fired === null) return 'Unknown';
  if (!confirmation.gate_fired) return 'Not asked';
  const decision = confirmation.decision ?? 'decided';
  return `Asked: ${decision}`;
};

// Only http(s) becomes a link. The server already keeps a link only if a tool
// result contained it verbatim; this stops anything else (javascript:, data:)
// from ever being rendered as a clickable href.
const isSafeLink = (link: string): boolean => /^https?:\/\//i.test(link);

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <span className="mb-1 block text-xs font-medium text-slate-500">
    {children}
  </span>
);

const ExecutionReceiptCard: React.FC<ExecutionReceiptCardProps> = ({
  receipt,
  terminalStatus = null,
}) => {
  const [auditOpen, setAuditOpen] = useState(false);

  const changed = receipt.changed_objects ?? [];
  const skipped = receipt.skipped_objects ?? [];
  const failed = receipt.failed_steps ?? [];
  const audit = receipt.verification ?? [];
  const status = receipt.status;
  // Outcome (the badge) and follow-up (below the summary) are separate questions:
  // a completed receipt can list a refused call and need nothing further.
  const followUp: ExecutionFollowUp | undefined =
    receipt.follow_up ??
    (terminalStatus === 'needs_new_plan' ? 'needs_new_plan' : undefined);
  const followUpReason = (receipt.follow_up_reason ?? '').trim();

  return (
    <Card
      className="my-1 rounded-lg border-2 border-slate-300 dark:border-slate-600"
      data-testid="execution-receipt-card"
    >
      <CardContent className="p-4">
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <p className="text-sm font-bold">Execution Receipt</p>
          <span
            className={`inline-block rounded border px-2 py-0.5 text-xs font-medium ${STATUS_STYLE[status] ?? STATUS_STYLE.cancelled}`}
            data-testid="execution-receipt-status"
          >
            {STATUS_LABEL[status] ?? status}
          </span>
        </div>

        <p className="mb-3 text-sm" data-testid="execution-receipt-summary">
          {receipt.summary}
        </p>

        {(followUp === 'needs_new_plan' || followUp === 'user_review') && (
          <div
            className={`mb-3 rounded border p-2 ${
              followUp === 'needs_new_plan'
                ? 'border-amber-400 bg-amber-50 dark:border-amber-600 dark:bg-amber-900/20'
                : 'border-slate-300 bg-slate-50 dark:border-slate-600 dark:bg-slate-800/40'
            }`}
            data-testid="execution-receipt-follow-up"
            data-follow-up={followUp}
          >
            <SectionTitle>Follow-up</SectionTitle>
            <p className="text-sm font-medium">
              {followUp === 'needs_new_plan'
                ? 'A new plan is needed for the remaining work.'
                : 'Please review the result.'}
            </p>
            {followUpReason && (
              <p
                className="mt-1 text-sm"
                data-testid="execution-receipt-follow-up-reason"
              >
                {followUpReason}
              </p>
            )}
          </div>
        )}

        {receipt.truncated && (
          <p
            className="mb-3 text-xs text-slate-500"
            data-testid="execution-receipt-truncated"
          >
            The full receipt was too large to store, so only the summary is
            shown.
          </p>
        )}

        {changed.length > 0 && (
          <div className="mb-3" data-testid="execution-receipt-changed">
            <SectionTitle>Changed ({changed.length})</SectionTitle>
            <div className="max-h-[240px] overflow-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-300 dark:border-gray-600">
                    <th className="px-2 py-1 text-left font-medium text-gray-700 dark:text-gray-300">
                      Type
                    </th>
                    <th className="px-2 py-1 text-left font-medium text-gray-700 dark:text-gray-300">
                      Name
                    </th>
                    <th className="px-2 py-1 text-left font-medium text-gray-700 dark:text-gray-300">
                      ID
                    </th>
                    <th className="px-2 py-1 text-left font-medium text-gray-700 dark:text-gray-300">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {changed.map((obj, i) => (
                    <tr
                      key={`${obj.id}-${i}`}
                      className="border-b border-gray-200 last:border-0 dark:border-gray-700"
                    >
                      <td className="px-2 py-1 text-gray-500 dark:text-gray-400">
                        {obj.type}
                      </td>
                      <td className="px-2 py-1 font-medium text-gray-700 dark:text-gray-300">
                        {obj.name}
                      </td>
                      <td className="px-2 py-1 text-gray-500 dark:text-gray-400">
                        {obj.link && isSafeLink(obj.link) ? (
                          <a
                            href={obj.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 underline"
                          >
                            {obj.id}
                            <ExternalLink
                              className="size-3 shrink-0"
                              aria-hidden="true"
                            />
                          </a>
                        ) : (
                          obj.id
                        )}
                      </td>
                      <td className="px-2 py-1">
                        <span className="inline-block rounded bg-gray-200 px-2 py-0.5 text-xs text-gray-600 dark:bg-gray-700 dark:text-gray-400">
                          {obj.action}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {skipped.length > 0 && (
          <div className="mb-3" data-testid="execution-receipt-skipped">
            <SectionTitle>Skipped on purpose ({skipped.length})</SectionTitle>
            <ul className="list-disc pl-5 text-sm">
              {skipped.map((obj, i) => (
                <li key={`${obj.id}-${i}`}>
                  <span className="font-medium">
                    {obj.name || obj.id || obj.type}
                  </span>
                  {': '}
                  {obj.reason}
                </li>
              ))}
            </ul>
          </div>
        )}

        {failed.length > 0 && (
          <div
            className="mb-3 rounded border border-red-300 bg-red-50 p-2 dark:border-red-800 dark:bg-red-900/20"
            data-testid="execution-receipt-failed"
          >
            <SectionTitle>Did not happen ({failed.length})</SectionTitle>
            <ul className="list-disc pl-5 text-sm">
              {failed.map((step, i) => (
                <li key={i}>
                  <span className="font-medium">{step.step}</span>
                  {': '}
                  {step.reason}
                </li>
              ))}
            </ul>
          </div>
        )}

        <p
          className="mb-1 text-xs text-slate-500"
          data-testid="execution-receipt-disclaimer"
        >
          This is the model&apos;s own account of the run. Check the listed IDs
          in the product before relying on it.
        </p>

        {audit.length > 0 && (
          <Collapsible open={auditOpen} onOpenChange={setAuditOpen}>
            <CollapsibleTrigger
              className="flex w-full cursor-pointer select-none items-center gap-2 border-none bg-transparent px-0 py-2 text-left [&[data-state=open]>svg:last-child]:rotate-0"
              aria-label={`Audit trail (${audit.length} calls)`}
            >
              <span className="flex-1 text-xs font-medium text-slate-600 dark:text-slate-300">
                Audit trail ({audit.length}{' '}
                {audit.length === 1 ? 'call' : 'calls'})
              </span>
              <ChevronUp
                className="size-3.5 shrink-0 rotate-180 text-slate-500 transition-transform duration-[280ms]"
                aria-hidden="true"
              />
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div
                className="max-h-[240px] overflow-auto"
                data-testid="execution-receipt-audit"
              >
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-300 dark:border-gray-600">
                      <th className="px-2 py-1 text-left font-medium text-gray-700 dark:text-gray-300">
                        Call
                      </th>
                      <th className="px-2 py-1 text-left font-medium text-gray-700 dark:text-gray-300">
                        Admission
                      </th>
                      <th className="px-2 py-1 text-left font-medium text-gray-700 dark:text-gray-300">
                        Approval
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {audit.map((entry, i) => (
                      <tr
                        key={`${entry.tool_call_id}-${i}`}
                        className="border-b border-gray-200 last:border-0 dark:border-gray-700"
                      >
                        <td className="px-2 py-1 font-medium text-gray-700 dark:text-gray-300">
                          {entry.action_type}
                        </td>
                        <td className="px-2 py-1">
                          {ADMISSION_LABEL[entry.admission] ?? entry.admission}
                        </td>
                        <td className="px-2 py-1 text-gray-500 dark:text-gray-400">
                          {approvalText(entry.confirmation)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CollapsibleContent>
          </Collapsible>
        )}
      </CardContent>
    </Card>
  );
};

export default ExecutionReceiptCard;
